import { useCallback, useEffect, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { FilterValues } from '@/hooks/use-filter-values';
import { DEFAULT_PAGE_SIZE } from '@/lib/constants/pagination';
import { getMaxLoadedPages } from '@/lib/query/get-max-loaded-pages';
import { PaginatedResponse } from '@/modules/common/types/pagination-types';

import { usePollingStaleness } from './use-polling-staleness';

/**
 * Poll cadence for the FCM fallback: an increasing backoff. Invoices/bills usually
 * finish in ~3-5s, so poll fast at first, then back off. The last entry (45s) repeats
 * until the in-progress item resolves and polling is disabled.
 */
const POLLING_BACKOFF_SCHEDULE_MS = [1_000, 5_000, 15_000, 45_000];

interface UsePollingListRefreshArgs<TItem extends { id: number | string }> {
  /** TanStack Query key prefix for the infinite list (e.g. ['invoices']). */
  queryKey: readonly unknown[];
  /** Applied filters — used to scope the peek cache and reset the baseline. */
  filters: FilterValues['values'];
  /** Items currently shown (from the authoritative list query). */
  items: TItem[] | undefined;
  /** Number of items currently loaded — drives the single-request page size. */
  loadedCount: number;
  /** `isFetching` from the list query — used to hide the banner after refresh. */
  isFetching: boolean;
  /** Gate: poll only when this is true (FCM unavailable AND in-progress items). */
  enabled: boolean;
  /** Fetches the whole currently-loaded set in one request at the given size. */
  fetchAll: (pageSize: number) => Promise<PaginatedResponse<TItem>>;
  /** Structural hash of the items (id + status is enough for the list view). */
  hashItems: (items: TItem[] | undefined) => string;
}

interface UsePollingListRefreshResult {
  /** True when a poll detected server-side changes and the banner should show. */
  hasPendingRefresh: boolean;
  /** Reloads the list (page 1, identical to the FCM refresh) and clears the flag. */
  refresh: () => void;
}

/**
 * FCM fallback for infinite-scroll list pages (invoices, bills).
 *
 * Mirrors `useFcmListRefresh`, but the trigger is a 15s background poll instead
 * of a silent push. The poll fetches the entire currently-loaded set in a single
 * request (page size = loaded count) and diffs it against what the user sees via
 * `usePollingStaleness`. When the server diverges, the same `RefreshData` banner
 * shows; clicking it resets the query to page 1 (the existing reload path).
 *
 * Intended to run only when notifications are disabled and the list contains at
 * least one in-progress item — pass that decision in via `enabled`.
 */
export const usePollingListRefresh = <TItem extends { id: number | string }>({
  queryKey,
  filters,
  items,
  loadedCount,
  isFetching,
  enabled,
  fetchAll,
  hashItems,
}: UsePollingListRefreshArgs<TItem>): UsePollingListRefreshResult => {
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const filterFingerprint = JSON.stringify(filters);

  const { hasStaleData, latestPeekRef, markAccepted } = usePollingStaleness<TItem>({
    enabled,
    filterFingerprint,
    authoritativeItems: items,
    peekQueryKey: [...queryKey, filters, 'poll'],
    fetchPeek: () => fetchAll(Math.max(loadedCount, DEFAULT_PAGE_SIZE)),
    hashItems,
    intervalSchedule: POLLING_BACKOFF_SCHEDULE_MS,
  });

  // Hide the banner once the triggered reset-fetch completes (mirrors useFcmListRefresh).
  useEffect(() => {
    if (isRefreshing && !isFetching) {
      setIsRefreshing(false);
    }
  }, [isRefreshing, isFetching]);

  const refresh = useCallback(() => {
    setIsRefreshing(true);
    // Reset to page 1 — identical reload to the FCM path.
    queryClient.resetQueries({ queryKey, exact: false });
    // Adopt the freshly-polled snapshot as the new baseline so the banner clears.
    markAccepted(latestPeekRef.current ?? []);
  }, [queryClient, queryKey, markAccepted, latestPeekRef]);

  // On staleness: refresh silently when ≤1 page is loaded; otherwise leave the flag set so
  // the banner shows (the user chooses when to reset a scrolled list). The ref limits this
  // to one auto-refresh per staleness episode, avoiding a resetQueries → poll → stale loop.
  const hasAutoRefreshedRef = useRef(false);
  useEffect(() => {
    if (!hasStaleData) {
      hasAutoRefreshedRef.current = false;
      return;
    }
    if (hasAutoRefreshedRef.current) return;

    if (getMaxLoadedPages(queryClient, [...queryKey]) <= 1) {
      hasAutoRefreshedRef.current = true;
      refresh();
    }
  }, [hasStaleData, queryClient, queryKey, refresh]);

  return { hasPendingRefresh: hasStaleData, refresh };
};
